import { useEffect, useRef, useState } from 'react';

import { metronome } from '../lib/metronome';

type UseCountdownTimerProps = {
    deadlineAt: number;
    onFinish?: () => void;
    updateInterval?: number;
};

type UseCountdownTimerReturn = {
    remaining: number;
};

function calculateRemaining(deadlineAt: number): number {
    return Math.max(0, deadlineAt - Date.now());
}

export function useCountdownTimer({
    deadlineAt,
    onFinish,
    updateInterval = 1000,
}: UseCountdownTimerProps): UseCountdownTimerReturn {
    const [remaining, setRemaining] = useState(calculateRemaining(deadlineAt));
    const hasFinishedRef = useRef(false);

    useEffect(() => {
        setRemaining(calculateRemaining(deadlineAt));
        hasFinishedRef.current = false;
    }, [deadlineAt]);

    useEffect(() => {
        if (remaining <= 0 && !hasFinishedRef.current) {
            hasFinishedRef.current = true;
            onFinish?.();
        }
    }, [remaining, onFinish]);

    useEffect(() => {
        const destroyMetronome = metronome(() => {
            setRemaining(calculateRemaining(deadlineAt));
        }, updateInterval);

        return () => {
            destroyMetronome();
        };
    }, [deadlineAt, updateInterval]);

    return { remaining };
}
